import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const T = {
  bg:'#07080c', bg2:'#0c0f18',
  border:'rgba(255,255,255,.07)',
  white:'#dce6fa', muted:'rgba(220,230,250,.45)', dim:'rgba(220,230,250,.22)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71', red:'#e74c3c',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ResetPasswordPage() {
  const [searchParams]          = useSearchParams();
  const [password,  setPassword]= useState('');
  const [confirm,   setConfirm] = useState('');
  const [showPass,  setShowPass]= useState(false);
  const [loading,   setLoading] = useState(false);
  const [done,      setDone]    = useState(false);
  const [error,     setError]   = useState('');
  const navigate = useNavigate();

  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  // Password strength indicator
  const strength = (() => {
    if (!password) return null;
    let score = 0;
    if (password.length >= 8)  score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 1) return { label:'Weak',   color:T.red    };
    if (score <= 3) return { label:'Fair',   color:'#e67e22' };
    return              { label:'Strong', color:T.green  };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    setLoading(true); setError('');

    try {
      const res  = await fetch(`${API}/api/auth/reset-password/`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch(err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width:'100%', padding:'11px 14px',
    background:T.bg2, border:`1px solid ${T.border}`,
    borderRadius:8, color:T.white,
    fontSize:14, fontFamily:T.sans, outline:'none',
    transition:'border-color .15s', boxSizing:'border-box',
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        input::placeholder { color:${T.dim}; }
        input:focus { border-color:rgba(0,200,240,.5)!important; outline:none; }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <div style={{
        minHeight:'100vh', background:T.bg, display:'flex',
        alignItems:'center', justifyContent:'center', padding:'24px',
        fontFamily:T.sans,
      }}>
        <div style={{ width:'100%', maxWidth:420 }}>

          <div style={{ textAlign:'center', marginBottom:28 }}>
            <Link to="/" style={{ textDecoration:'none' }}>
              <div style={{ fontSize:22, fontWeight:900, color:T.white, letterSpacing:'-.02em' }}>
                Tylent<span style={{ color:T.cyan }}>ed</span>
              </div>
            </Link>
          </div>

          <div style={{
            background:'rgba(255,255,255,.025)', border:`1px solid ${T.border}`,
            borderRadius:16, padding:'28px 26px',
          }}>
            {done ? (
              <div style={{ textAlign:'center', padding:'8px 0' }}>
                <div style={{ fontSize:36, marginBottom:14 }}>✅</div>
                <h2 style={{ fontSize:18, fontWeight:800, color:T.white, marginBottom:8 }}>
                  Password updated!
                </h2>
                <p style={{ fontSize:13.5, color:T.muted, lineHeight:1.7, marginBottom:16 }}>
                  Your password has been reset. Redirecting you to sign in…
                </p>
                <Link to="/login" style={{
                  padding:'10px 24px', borderRadius:8,
                  background:`linear-gradient(135deg,${T.cyan},${T.blue})`,
                  color:'#07080c', fontSize:13.5, fontWeight:700,
                  textDecoration:'none', display:'inline-block',
                }}>Sign in now →</Link>
              </div>
            ) : (
              <>
                <h2 style={{ fontSize:18, fontWeight:800, color:T.white, marginBottom:6 }}>
                  Set new password
                </h2>
                <p style={{ fontSize:13, color:T.muted, marginBottom:22, lineHeight:1.6 }}>
                  Choose a strong password for your Tylented account.
                </p>

                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom:14 }}>
                    <label style={{ fontSize:12, color:T.muted, display:'block', marginBottom:5 }}>
                      New password
                    </label>
                    <div style={{ position:'relative' }}>
                      <input
                        type={showPass?'text':'password'} value={password}
                        onChange={e=>setPassword(e.target.value)}
                        placeholder="At least 8 characters" required
                        style={{ ...inputStyle, paddingRight:44 }}
                      />
                      <button type="button" onClick={()=>setShowPass(p=>!p)} style={{
                        position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                        background:'none', border:'none', color:T.muted, cursor:'pointer', fontSize:14,
                      }}>{showPass ? '🙈' : '👁'}</button>
                    </div>
                    {/* Strength indicator */}
                    {strength && (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:7 }}>
                        <div style={{ flex:1, height:3, borderRadius:2, background:'rgba(255,255,255,.08)' }}>
                          <div style={{
                            height:'100%', borderRadius:2, background:strength.color,
                            width: strength.label==='Weak'?'33%': strength.label==='Fair'?'66%':'100%',
                            transition:'width .3s, background .3s',
                          }}/>
                        </div>
                        <span style={{ fontSize:11, color:strength.color, fontWeight:600, minWidth:40 }}>
                          {strength.label}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom:20 }}>
                    <label style={{ fontSize:12, color:T.muted, display:'block', marginBottom:5 }}>
                      Confirm new password
                    </label>
                    <input
                      type="password" value={confirm}
                      onChange={e=>setConfirm(e.target.value)}
                      placeholder="Repeat your password" required
                      style={{
                        ...inputStyle,
                        borderColor: confirm && confirm !== password ? `${T.red}60` : T.border,
                      }}
                    />
                    {confirm && confirm !== password && (
                      <div style={{ fontSize:11.5, color:T.red, marginTop:5 }}>
                        Passwords do not match
                      </div>
                    )}
                  </div>

                  {error && (
                    <div style={{
                      padding:'9px 13px', marginBottom:14, borderRadius:7,
                      background:`${T.red}12`, border:`1px solid ${T.red}40`,
                      fontSize:12.5, color:T.red,
                    }}>{error}</div>
                  )}

                  <button type="submit" disabled={loading || !token} style={{
                    width:'100%', padding:'12px', border:'none', borderRadius:8,
                    background: (loading || !token) ? `${T.cyan}40` : `linear-gradient(135deg,${T.cyan},${T.blue})`,
                    color:'#07080c', fontSize:14, fontWeight:700, fontFamily:T.sans,
                    cursor: (loading || !token) ? 'default' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  }}>
                    {loading && <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(0,0,0,.3)', borderTopColor:'#07080c', animation:'spin .65s linear infinite' }}/>}
                    {loading ? 'Updating…' : 'Update password'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
